package com.mgacreative.hotel.domain.repository

import com.mgacreative.hotel.core.network.SupabaseNetworkClient
import com.mgacreative.hotel.core.tenant.TenantSession
import com.mgacreative.hotel.domain.demo.DemoDataProvider
import com.mgacreative.hotel.domain.model.Reservation
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

interface ReservationRepository {
    suspend fun getReservations(): List<Reservation>
    suspend fun createReservation(reservation: Reservation): Reservation?
    suspend fun updateReservation(reservation: Reservation): Reservation?
    suspend fun updateReservationStatusAndRoom(id: String, status: String, roomId: String?)
    suspend fun updateKbsStatus(reservationId: String, kbsStatus: String, kbsSentAt: String?)
    suspend fun transferFolioToNewRoom(reservationId: String, newRoomId: String)
    suspend fun checkOutAndPay(reservation: Reservation)
    suspend fun deleteReservation(reservationId: String)
}

class ReservationRepositoryImpl(
    private val networkClient: SupabaseNetworkClient,
    private val tenantSession: TenantSession? = null
) : ReservationRepository {
    
    override suspend fun getReservations(): List<Reservation> {
        if (tenantSession?.isDemoMode == true) {
            return DemoDataProvider.reservationsState.value
        }
        return try {
            networkClient.client.postgrest["reservations"]
                .select(io.github.jan.supabase.postgrest.query.Columns.raw("*, guests(*), rooms(*)"))
                .decodeList<Reservation>()
        } catch (e: Exception) {
            e.printStackTrace()
            try {
                networkClient.client.postgrest["reservations"]
                    .select()
                    .decodeList<Reservation>()
            } catch (e2: Exception) {
                networkClient.handleError(e2)
                emptyList()
            }
        }
    }

    override suspend fun createReservation(reservation: Reservation): Reservation? {
        if (tenantSession?.isDemoMode == true) {
            val newRes = reservation.copy(id = reservation.id ?: "demo-res-${(100..999).random()}")
            DemoDataProvider.reservationsState.value = DemoDataProvider.reservationsState.value + newRes
            return newRes
        }
        return try {
            networkClient.client.postgrest["reservations"]
                .insert(reservation) {
                    select() // Return the inserted row
                }
                .decodeSingleOrNull<Reservation>()
        } catch (e: Exception) {
            println("RES_REPO_ERROR: ${e.message}")
            e.printStackTrace()
            networkClient.handleError(e)
            null
        }
    }

    override suspend fun updateReservation(reservation: Reservation): Reservation? {
        if (tenantSession?.isDemoMode == true) {
            DemoDataProvider.reservationsState.value = DemoDataProvider.reservationsState.value.map {
                if (it.id == reservation.id) reservation else it
            }
            return reservation
        }
        return try {
            @kotlinx.serialization.Serializable
            data class UpdateReservationDto(
                @kotlinx.serialization.SerialName("check_in_date") val checkInDate: String,
                @kotlinx.serialization.SerialName("check_out_date") val checkOutDate: String,
                @kotlinx.serialization.SerialName("room_type") val roomType: String?,
                @kotlinx.serialization.SerialName("special_notes") val specialNotes: String?,
                @kotlinx.serialization.SerialName("adults_count") val adultsCount: Int,
                @kotlinx.serialization.SerialName("children_count") val childrenCount: Int,
                @kotlinx.serialization.SerialName("total_price") val totalPrice: Double,
                @kotlinx.serialization.SerialName("room_id") val roomId: String?
            )
            val dto = UpdateReservationDto(
                checkInDate = reservation.checkInDate,
                checkOutDate = reservation.checkOutDate,
                roomType = reservation.roomType,
                specialNotes = reservation.specialNotes,
                adultsCount = reservation.adultsCount,
                childrenCount = reservation.childrenCount,
                totalPrice = reservation.totalPrice,
                roomId = reservation.roomId
            )
            
            networkClient.client.postgrest["reservations"]
                .update(dto) {
                    filter {
                        eq("id", reservation.id ?: "")
                    }
                    select()
                }
                .decodeSingleOrNull<Reservation>()
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            null
        }
    }

    override suspend fun updateReservationStatusAndRoom(id: String, status: String, roomId: String?) {
        if (tenantSession?.isDemoMode == true) {
            DemoDataProvider.reservationsState.value = DemoDataProvider.reservationsState.value.map {
                if (it.id == id) it.copy(status = status, roomId = roomId) else it
            }
            return
        }
        try {
            @kotlinx.serialization.Serializable
            data class UpdatePayload(
                val status: String,
                @kotlinx.serialization.SerialName("room_id") val roomId: String?
            )
            
            networkClient.client.postgrest["reservations"]
                .update(UpdatePayload(status, roomId)) {
                    filter {
                        eq("id", id)
                    }
                }
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
        }
    }

    override suspend fun updateKbsStatus(reservationId: String, kbsStatus: String, kbsSentAt: String?) {
        if (tenantSession?.isDemoMode == true) {
            val currentList = DemoDataProvider.reservationsState.value
            val updated = currentList.map {
                if (it.id == reservationId) it.copy(kbsStatus = kbsStatus, kbsSentAt = kbsSentAt) else it
            }
            DemoDataProvider.reservationsState.value = updated
            return
        }
        try {
            @kotlinx.serialization.Serializable
            data class KbsUpdatePayload(
                @kotlinx.serialization.SerialName("kbs_status") val kbsStatus: String,
                @kotlinx.serialization.SerialName("kbs_sent_at") val kbsSentAt: String?
            )
            networkClient.client.postgrest["reservations"]
                .update(KbsUpdatePayload(kbsStatus, kbsSentAt)) {
                    filter {
                        eq("id", reservationId)
                    }
                }
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
        }
    }

    override suspend fun transferFolioToNewRoom(reservationId: String, newRoomId: String) {
        if (tenantSession?.isDemoMode == true) return
        try {
            @kotlinx.serialization.Serializable
            data class FolioUpdateRequest(val room_id: String)
            
            networkClient.client.postgrest["folio_items"].update(
                FolioUpdateRequest(newRoomId)
            ) {
                filter {
                    eq("reservation_id", reservationId)
                }
            }
        } catch (e: Exception) {
            e.printStackTrace()
            // Ignore if folio_items doesn't exist yet or no items found
        }
    }

    // Call this when a guest checks out to mark as CHECKED_OUT and record the income.
    override suspend fun checkOutAndPay(reservation: Reservation) {
        if (tenantSession?.isDemoMode == true) {
            updateReservationStatusAndRoom(reservation.id ?: "", "CHECKED_OUT", reservation.roomId)
            val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date.toString()
            val newMovement = com.mgacreative.hotel.domain.model.CashMovement(
                id = "cm-${(100..999).random()}",
                tenant_id = DemoDataProvider.DEMO_TENANT_ID,
                type = "INCOME",
                amount = reservation.totalPrice,
                description = "Oda Ödemesi (${reservation.rooms?.roomNumber ?: ""})",
                movement_date = today,
                created_at = today
            )
            DemoDataProvider.cashMovementsState.value = listOf(newMovement) + DemoDataProvider.cashMovementsState.value
            return
        }
        try {
            // 1. Mark as CHECKED_OUT
            updateReservationStatusAndRoom(reservation.id ?: "", "CHECKED_OUT", reservation.roomId)
            
            // 2. Record Income in cash_movements
            val tenantId = reservation.tenantId
            val totalAmount = reservation.totalPrice
            
            if (totalAmount > 0) {
                @kotlinx.serialization.Serializable
                data class MovementRequest(
                    val tenant_id: String,
                    val type: String,
                    val amount: Double,
                    val description: String,
                    val movement_date: String
                )
                
                val today = kotlinx.datetime.Clock.System.now().toLocalDateTime(kotlinx.datetime.TimeZone.currentSystemDefault()).date.toString()
                
                val request = MovementRequest(
                    tenant_id = tenantId,
                    type = "INCOME",
                    amount = totalAmount,
                    description = "Oda Ödemesi",
                    movement_date = today
                )
                
                networkClient.client.postgrest["cash_movements"].insert(request)
            }
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
        }
    }
    override suspend fun deleteReservation(reservationId: String) {
        if (tenantSession?.isDemoMode == true) {
            DemoDataProvider.reservationsState.value = DemoDataProvider.reservationsState.value.filterNot { it.id == reservationId }
            return
        }
        try {
            networkClient.client.postgrest["reservations"]
                .delete {
                    filter {
                        eq("id", reservationId)
                    }
                }
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            throw e
        }
    }
}
